'use client';

import Steps from './Steps/ConfiguratorSteps';
import { useEffect, useState } from 'react';
import { stepsData } from './stepsData';
import CarType from './CarType/ConfiguratorCarType';
import Version from './Version/ConfiguratorVersion';
import Colour from './Colour/ConfiguratorColour';
import Interior from './Interior/ConfiguratorInterior';
import Summary from './Summary/ConfiguratorSummary';
import ChooseBrand from '../ChooseBrand/ChooseBrand';
import TitleBlock from '../TitleBlock/ConfiguratorTitleBlock';

// 数据适配器：将Sitecore数据转换为老项目格式
// 增加 selectedVersionTitle，用于在有多个版本时精确控制当前版本，从而驱动颜色/内饰列表
const transformSitecoreToStepsData = (
  sitecoreData,
  selectedBrand = null,
  selectedCarTitle = null,
  selectedVersionTitle = null
) => {
  if (!sitecoreData) return stepsData;

  const {
    summaryItems,
    oneCategory,
    oneStepName,
    oneStepTitle,
    oneStepButtonText,
    twoCategory,
    twoStepName,
    twoStepTitle,
    twoStepButtonText,
    threeCategory,
    threeStepName,
    threeStepTitle,
    threeStepButtonText,
    fourCategory,
    fourStepName,
    fourStepTitle,
    fourStepButtonText,
    fiveCategory,
    fiveStepName,
    fiveStepTitle,
    fiveStepButtonText,
    priceUnit,
    summaryLastLabel,
    summaryLastButtonText,
    summaryLastButtonUrl,
    financeCalculatorUrl,
    noPriceText,
  } = sitecoreData;

  // 如果没有选择品牌，返回空数据
  if (!selectedBrand) {
    return [];
  }

  // 获取选中品牌的车型数�?
  const selectedBrandData = sitecoreData.carType?.find((brand) => brand.name === selectedBrand);

  const carModels = selectedBrandData?.fields?.carModel || [];

  // 帮助函数：根据当前选中车型与版本，获取实际要使用的颜色列表
  const getVersionColourList = (car) => {
    if (!car?.fields) return [];

    const carColourList = car.fields.colourList || [];
    const versions = car.fields.versionList || [];

    // 查找当前选中的版本（如果有）
    let selectedVersion;

    // 1. 优先使用传入的 selectedVersionTitle（来自前端用户选择）
    if (selectedVersionTitle) {
      selectedVersion = versions.find((v) => v.fields?.title?.value === selectedVersionTitle);
    }

    // 2. 如果没有传入或没匹配到，则回退到 Sitecore 配置的 selected 版本
    if (!selectedVersion) {
      selectedVersion = versions.find((v) => v.fields?.selected?.value);
    }

    // 如果选中的版本下有颜色列表，则优先使用版本下的颜色
    if (selectedVersion?.fields?.colourList?.length) {
      return selectedVersion.fields.colourList;
    }

    // 否则回退到车型自身的颜色列表
    return carColourList;
  };

  // 规范化颜色列表：如果没有任何颜色被选中，则默认选中第一个
  const normalizeColourList = (list) => {
    if (!Array.isArray(list) || list.length === 0) return [];

    const hasSelected = list.some((color) => color?.fields?.selected?.value);
    if (hasSelected) {
      return list;
    }

    // 没有显式选中项时，克隆列表并将第一个标记为选中
    const [first, ...rest] = list;
    const firstWithSelected = {
      ...first,
      fields: {
        ...first.fields,
        selected: {
          ...(first.fields?.selected || {}),
          value: true,
        },
      },
    };
    return [firstWithSelected, ...rest];
  };

  // 构建步骤数据
  const steps = [
    // Car Type步骤 - 现在显示选中品牌下的车型
    {
      id: 1,
      stepValue: 1,
      stepName: oneStepName?.value || 'Car type',
      title: oneStepTitle?.value || 'Select car type',
      activeStep: true,
      category: oneCategory?.value || 'carType',
      buttonText: oneStepButtonText?.value || 'Continue',
      priceUnit: priceUnit?.value || '£',
      items: carModels.map((car, index) => ({
        id: 11 + index,
        title: car.fields.title?.value || '',
        selected: car.fields.selected?.value || false,
        image: car.fields.contentBgImage?.value || null,
        mobileImage: car.fields.mobileContentBgImage?.value || null,
        category: car.fields.versionList?.length > 0 ? 'secondCar' : 'firstCar',
        price: car.fields.price?.value || 0,
        priceText: car.fields.priceText?.value || '', // 新增priceText字段
        EnquireCarModel: car.fields.EnquireCarModel?.value || '', // 新增EnquireCarModel字段
        finalCarUrl: car.fields.finalCarUrl?.value || null, // 新增finalCarUrl字段
        versionList: car.fields.versionList || [],
        colourList: car.fields.colourList || [],
        carPdpUrl: car.fields.carPdpUrl?.value || null,
      })),
    },
  ];

  // 根据车型决定是否添加Version步骤
  // 修复：优先使用传入的 selectedCarTitle，如果没有则使用默认选中的车型
  let selectedCar;
  if (selectedCarTitle) {
    // 使用传入的车型标题来查找车型
    selectedCar = carModels.find((car) => car.fields.title?.value === selectedCarTitle);
  } else {
    // 回退到默认逻辑：查找默认选中的车�?
    selectedCar = carModels.find((car) => car.fields.selected?.value);
  }

  const versionsForSelectedCar = selectedCar?.fields.versionList || [];

  if (versionsForSelectedCar.length > 0) {
    steps.push({
      id: 2,
      stepValue: 2,
      stepName: twoStepName?.value || 'Version',
      title: `${selectedCar.fields.title?.value || 'Car'} Version`, // 显示当前选中车型的名�?
      activeStep: false,
      category: 'version',
      carTypeId: selectedCar.id, // 添加车型ID标识
      carTypeName: selectedCar.fields.title?.value, // 添加车型名称标识
      buttonText: twoStepButtonText?.value || 'Continue',
      priceUnit: priceUnit?.value || '£',
      items: versionsForSelectedCar.map((version, index) => ({
        id: 21 + index, // 为当前选中车型的version创建ID
        title: version.fields.title?.value || '',
        price: version.fields.price?.value || 0,
        // 如果传入了 selectedVersionTitle，则以此为准，否则回退到 Sitecore 配置
        selected: selectedVersionTitle
          ? version.fields.title?.value === selectedVersionTitle
          : version.fields.selected?.value || false,
        innerItems:
          version.fields.innerItems?.map((item) => ({
            id: item.fields.id?.value || Math.random(),
            text: item.fields.text?.value || '',
          })) || [],
      })),
    });
  } else {
  }

  // Colour步骤
  const rawCarColourList = selectedCar?.fields.colourList || [];
  const rawVersionColourList = getVersionColourList(selectedCar);

  const carColourList = normalizeColourList(rawCarColourList);
  const versionColourList = normalizeColourList(rawVersionColourList);

  steps.push({
    id: 3,
    stepValue: 3,
    stepName: threeStepName?.value || 'Colour',
    title: threeStepTitle?.value || 'Select colour',
    activeStep: false,
    category: threeCategory?.value || 'colour',
    buttonText: threeStepButtonText?.value || 'Continue',
    priceUnit: priceUnit?.value || '£',
    noPriceText: noPriceText?.value || '',
    carTypes: {
      firstCar: {
        category: 'firstCar',
        selected: selectedCar?.fields.versionList?.length === 0, // 根据当前选中车型动态设�?
        items:
          selectedCar?.fields.versionList?.length === 0
            ? carColourList.map((color, index) => ({
                id: 31 + index,
                selected: color.fields.selected?.value || false,
                color: color.fields.color?.value || null,
                image: color.fields.image?.value || null,
                colorName: color.fields.colorName?.value || '',
                price: color.fields.price?.value || 0,
                inclVAT: color.fields.inclVAT?.value || '',
                interiors: color.fields.interiorList?.map((interior, index) => ({
                  id: 41 + index,
                  selected: interior.fields.selected?.value || false,
                  color: interior.fields.color?.value || null,
                  image: interior.fields.image?.value || null,
                  interiorName: interior.fields.interiorName?.value || '',
                  price: interior.fields.price?.value || 0,
                  inclVAT: interior.fields.inclVAT?.value || '',
                })),
              })) || []
            : [],
      },
      secondCar: {
        category: 'secondCar',
        selected: selectedCar?.fields.versionList?.length > 0, // 根据当前选中车型动态设�?
        items:
          selectedCar?.fields.versionList?.length > 0
            ? versionColourList.map((color, index) => ({
                id: 31 + index,
                selected: color.fields.selected?.value || false,
                color: color.fields.color?.value || null,
                image: color.fields.image?.value || null,
                colorName: color.fields.colorName?.value || '',
                price: color.fields.price?.value || 0,
                inclVAT: color.fields.inclVAT?.value || '',
                interiors: color.fields.interiorList?.map((interior, index) => ({
                  id: 41 + index,
                  selected: interior.fields.selected?.value || false,
                  color: interior.fields.color?.value || null,
                  image: interior.fields.image?.value || null,
                  interiorName: interior.fields.interiorName?.value || '',
                  price: interior.fields.price?.value || 0,
                  inclVAT: interior.fields.inclVAT?.value || '',
                })),
              })) || []
            : [],
      },
    },
  });

  // Interior 步骤
  steps.push({
    id: 4,
    stepValue: 4,
    stepName: fourStepName?.value || 'Interior',
    title: fourStepTitle?.value || 'Select interior',
    activeStep: false,
    category: fourCategory?.value || 'Interior',
    buttonText: fourStepButtonText?.value || 'Continue',
    priceUnit: priceUnit?.value || '£',
    noPriceText: noPriceText?.value || '',
    carTypes: {
      firstCar: {
        category: 'firstCar',
        selected: selectedCar?.fields.versionList?.length === 0, // 根据当前选中车型动态设�?
        items:
          selectedCar?.fields.versionList?.length === 0
            ? carColourList.map((color, index) => ({
                id: 31 + index,
                selected: color.fields.selected?.value || false,
                color: color.fields.color?.value || null,
                image: color.fields.image?.value || null,
                colorName: color.fields.colorName?.value || '',
                price: color.fields.price?.value || 0,
                inclVAT: color.fields.inclVAT?.value || '',
                interiors: color.fields.interiorList?.map((interior, index) => ({
                  id: 41 + index,
                  selected: interior.fields.selected?.value || false,
                  color: interior.fields.color?.value || null,
                  image: interior.fields.image?.value || null,
                  interiorName: interior.fields.interiorName?.value || '',
                  price: interior.fields.price?.value || 0,
                  inclVAT: interior.fields.inclVAT?.value || '',
                })),
              })) || []
            : [],
      },
      secondCar: {
        category: 'secondCar',
        selected: selectedCar?.fields.versionList?.length > 0, // 根据当前选中车型动态设�?
        items:
          selectedCar?.fields.versionList?.length > 0
            ? versionColourList.map((color, index) => ({
                id: 31 + index,
                selected: color.fields.selected?.value || false,
                color: color.fields.color?.value || null,
                image: color.fields.image?.value || null,
                colorName: color.fields.colorName?.value || '',
                price: color.fields.price?.value || 0,
                inclVAT: color.fields.inclVAT?.value || '',
                interiors: color.fields.interiorList?.map((interior, index) => ({
                  id: 41 + index,
                  selected: interior.fields.selected?.value || false,
                  color: interior.fields.color?.value || null,
                  image: interior.fields.image?.value || null,
                  interiorName: interior.fields.interiorName?.value || '',
                  price: interior.fields.price?.value || 0,
                  inclVAT: interior.fields.inclVAT?.value || '',
                })),
              })) || []
            : [],
      },
    },
  });

  // Summary步骤
  steps.push({
    id: 5,
    stepValue: 5,
    stepName: fiveStepName?.value || 'Summary',
    title: fiveStepTitle?.value || 'Summary',
    activeStep: false,
    category: fiveCategory?.value || 'summary',
    buttonText: fiveStepButtonText?.value || 'Finance your car',
    priceUnit: priceUnit?.value || '£',
    summaryLastLabel: summaryLastLabel?.value || 'Want to buy this configuration?',
    summaryLastButtonText: summaryLastButtonText?.value || 'Enquire',
    summaryLastButtonUrl: summaryLastButtonUrl?.value || { href: '/contact-us' },
    financeCalculatorUrl: financeCalculatorUrl?.value || { href: '/finance-calculator' },
    items: summaryItems?.map((item, index) => ({
      id: 51 + index,
      text: item.fields.text?.value || '',
      category: item.fields.category?.value || '',
      value: item.fields.value?.value || '', // 直接配置的�?
      valueSource: item.fields.valueSource?.value || '', // 值来源（carType, version, colour, estimatedPrice�?
    })) || [
      { id: 51, text: 'Car type', category: 'carType', valueSource: 'carType' },
      { id: 52, text: 'Version', category: 'version', valueSource: 'version' },
      { id: 53, text: 'Colour', category: 'colour', valueSource: 'colour' },
      {
        id: 54,
        text: 'Estimated price',
        category: 'estimatedPrice',
        valueSource: 'estimatedPrice',
      },
    ],
  });

  return steps;
};

export default function Main({ sitecoreData }) {
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showBrandSelection, setShowBrandSelection] = useState(true);
  const [stepList, setSteps] = useState([]);
  const [removedElementState, setRemovedElementState] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);

  // 当选择品牌时，更新步骤数据
  useEffect(() => {
    if (selectedBrand) {
      // 初始加载时，使用默认选中的车型（不传�?selectedCarTitle 参数�?
      const initialStepsData = transformSitecoreToStepsData(sitecoreData, selectedBrand);
      setSteps(initialStepsData);
    }
  }, [selectedBrand, sitecoreData]);

  const carType = stepList?.find((item) => item.stepValue === 1);
  const selectedCategoryCar = carType?.items?.find((item) => item.selected)?.category || '';
  const currentStep = stepList.find((el) => el.activeStep);
  useEffect(() => {
    if (currentStep?.stepValue === 3) {
      const list = currentStep?.carTypes[selectedCategoryCar].items;
      const selected = list?.find((item) => item.selected);
      if (selected) {
        setSelectedColor(selected.id);
      }
    }
  }, [currentStep]);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand.id);
    setShowBrandSelection(false);
  };

  const handleBackToBrandSelection = () => {
    setShowBrandSelection(true);
    setSelectedBrand(null);
    setSteps([]);
  };

  const secondCarSelected = carType?.items?.some(
    (item) => item.selected && item.category === 'secondCar'
  );

  const handleClickStep = (stepIndex) => {
    // 如果点击的是当前步骤，不做任何操�?
    if (stepIndex === currentStep?.stepValue) {
      return;
    }

    // 如果点击的是下一步（Continue按钮），处理步骤跳转逻辑
    if (stepIndex === currentStep?.stepValue + 1) {
      if (currentStep?.stepValue === 1) {
        // 从carType步骤跳转到下一�?
        const selectedItem = stepList
          .find((step) => step.stepValue === 1)
          ?.items?.find((item) => item.selected);

        if (selectedItem?.category === 'secondCar') {
          // 选择secondCar，确保version步骤存在并激�?
          let updatedSteps = [...stepList];

          // 检查version步骤是否存在
          const hasVersionStep = updatedSteps.some((step) => step.stepValue === 2);

          if (!hasVersionStep) {
            // 如果version步骤不存在，从初始数据中获取version步骤
            const initialStepsData = transformSitecoreToStepsData(sitecoreData, selectedBrand);
            const versionStep = initialStepsData.find((step) => step.stepValue === 2);

            if (versionStep) {
              // 在carType和colour之间插入version步骤
              updatedSteps.splice(1, 0, versionStep);

              // 重新调整后续步骤的id和stepValue
              updatedSteps.forEach((step, index) => {
                if (index >= 2) {
                  step.id = index + 1;
                  step.stepValue = index + 1;
                }
              });
            }
          }

          // 激活version步骤，停用carType步骤
          updatedSteps = updatedSteps.map((step) => {
            if (step.stepValue === 2) {
              return { ...step, activeStep: true };
            } else if (step.stepValue === 1) {
              return { ...step, activeStep: false };
            }
            return step;
          });

          setSteps(updatedSteps);
        } else if (selectedItem?.category === 'firstCar') {
          // 选择firstCar，移除version步骤并直接跳到colour步骤
          let updatedSteps = stepList.filter((step) => step.stepValue !== 2);

          // 重新调整后续步骤的id和stepValue
          updatedSteps.forEach((step, index) => {
            if (index >= 1) {
              step.id = index + 1;
              step.stepValue = index + 1;
            }
          });

          // 激活colour步骤，停用carType步骤
          updatedSteps = updatedSteps.map((step) => {
            if (step.stepValue === 3) {
              return { ...step, activeStep: true };
            } else if (step.stepValue === 1) {
              return { ...step, activeStep: false };
            }
            return step;
          });

          setSteps(updatedSteps);
        }
      } else if (currentStep?.stepValue === 2) {
        // 从version步骤跳转到colour步骤
        const updatedSteps = stepList.map((step) => {
          if (step.stepValue === 3) {
            return { ...step, activeStep: true };
          } else if (step.stepValue === 2) {
            return { ...step, activeStep: false };
          }
          return step;
        });
        setSteps(updatedSteps);
      } else {
        // 其他步骤的正常跳�?
        setSteps((prevSteps) =>
          prevSteps.map((item, index) => ({
            ...item,
            activeStep: index + 1 === stepIndex,
          }))
        );
      }
    } else {
      // 点击其他步骤，直接跳�?
      setSteps((prevSteps) =>
        prevSteps.map((item, index) => ({
          ...item,
          activeStep: index + 1 === stepIndex,
        }))
      );
    }
  };

  // 添加car type步骤的点击处�?
  const handleClickItem = (itemId) => {
    const newStepsData = stepList.map((step) => {
      if (step.stepValue === currentStep?.stepValue) {
        return {
          ...step,
          items: step.items?.map((item) => ({
            ...item,
            selected: item.id === itemId,
          })),
        };
      }
      return step;
    });

    // 更新步骤数据
    setSteps(newStepsData);

    // 重要：当车型选择改变时，需要重新构建整个steps数据
    // 因为不同车型有不同的version选项
    const selectedCar = newStepsData
      .find((step) => step.stepValue === 1)
      ?.items?.find((item) => item.selected);

    if (selectedCar) {
      // 重新构建steps数据，确保version步骤显示当前选中车型的选项
      const updatedStepsData = transformSitecoreToStepsData(
        sitecoreData,
        selectedBrand,
        selectedCar.title
      );

      // 保持当前车型的选择状�?
      const finalStepsData = updatedStepsData.map((step) => {
        if (step.stepValue === 1) {
          return {
            ...step,
            items: step.items?.map((item) => ({
              ...item,
              selected: item.id === itemId,
            })),
          };
        }
        return step;
      });

      setSteps(finalStepsData);
    }
  };

  const handleClickItemColour = (itemId) => {
    const newStepsData = stepList.map((item) => {
      if (item.stepValue === currentStep?.stepValue) {
        return {
          ...item,
          carTypes: {
            ...item.carTypes,
            [selectedCategoryCar]: {
              ...item.carTypes[selectedCategoryCar],
              items: item.carTypes[selectedCategoryCar].items.map((el) => {
                return {
                  ...el,
                  selected: el.id === itemId,
                };
              }),
            },
          },
        };
      }
      return item;
    });
    setSelectedColor(itemId);
    setSteps(newStepsData);
  };
  const handleClickItemInterior = (itemId) => {
    const newStepsData = stepList.map((item) => {
      if (item.stepValue === currentStep?.stepValue) {
        return {
          ...item,
          carTypes: {
            ...item.carTypes,
            [selectedCategoryCar]: {
              ...item.carTypes[selectedCategoryCar],
              items: item.carTypes[selectedCategoryCar].items.map((el) => {
                return {
                  ...el,
                  interiors: el.selected
                    ? el.interiors.map((interior) => {
                        return {
                          ...interior,
                          selected: interior.id === itemId,
                        };
                      })
                    : el.interiors.map((interior) => {
                        return {
                          ...interior,
                          selected: false,
                        };
                      }),
                };
              }),
            },
          },
        };
      }
      return item;
    });
    setSteps(newStepsData);
  };

  // 添加version步骤的点击处�?
  const handleClickVersionItem = (itemId) => {
    // 获取当前选中的车型
    const selectedCar = carType?.items?.find((item) => item.selected);
    if (!selectedCar) return;

    // 记录当前激活的步骤，避免重建 steps 后总是回到第一步
    const previousActiveStepValue = currentStep?.stepValue || 2; // 默认留在版本步骤

    // 找到当前车型对应的 version 步骤
    const versionStep = stepList.find(
      (step) => step.stepValue === 2 && step.carTypeName === selectedCar.title
    );
    const clickedVersion = versionStep?.items?.find((item) => item.id === itemId);

    // 先在当前 steps 中更新版本选中状态
    const newStepsData = stepList.map((step) => {
      if (step.stepValue === 2 && step.carTypeName === selectedCar.title) {
        return {
          ...step,
          items: step.items?.map((item) => ({
            ...item,
            selected: item.id === itemId,
          })),
        };
      }
      return step;
    });
    setSteps(newStepsData);

    // 然后基于最新的车型 + 版本选择，重新构建 steps，
    // 以便 Colour / Interior 步骤中的颜色列表跟随版本切换
    if (clickedVersion) {
      const updatedStepsData = transformSitecoreToStepsData(
        sitecoreData,
        selectedBrand,
        selectedCar.title,
        clickedVersion.title
      );

      // 保持当前车型和版本的选中状态，并保持激活步骤不变
      const finalStepsData = updatedStepsData.map((step) => {
        const isActive = step.stepValue === previousActiveStepValue;

        if (step.stepValue === 1) {
          return {
            ...step,
            activeStep: isActive,
            items: step.items?.map((item) => ({
              ...item,
              selected: item.title === selectedCar.title,
            })),
          };
        }
        if (step.stepValue === 2 && step.carTypeName === selectedCar.title) {
          return {
            ...step,
            activeStep: isActive,
            items: step.items?.map((item) => ({
              ...item,
              selected: item.title === clickedVersion.title,
            })),
          };
        }

        return {
          ...step,
          activeStep: isActive,
        };
      });

      setSteps(finalStepsData);
    }
  };

  const getStep = (step, category, activeStep, index) => {
    // 使用 stepValue 而不是硬编码�?category 来匹配步�?
    if (step.stepValue === 1 && activeStep) {
      return (
        <CarType
          step={step}
          index={index}
          onClickSelectType={handleClickItem}
          onClickStep={handleClickStep}
        />
      );
    }
    if (step.stepValue === 2 && activeStep) {
      return (
        <Version
          step={step}
          index={index}
          onClickVersionItem={handleClickVersionItem}
          onClickStep={handleClickStep}
        />
      );
    }
    if (step.stepValue === 3 && activeStep) {
      return (
        <Colour
          step={step}
          index={index}
          selectedCategoryCar={selectedCategoryCar}
          onClickColourItem={handleClickItemColour}
          onClickStep={handleClickStep}
          sitecoreData={sitecoreData}
        />
      );
    }
    if (step.stepValue === 4 && activeStep) {
      return (
        <Interior
          step={step}
          index={index}
          selectedCategoryCar={selectedCategoryCar}
          selectedColor={selectedColor}
          onClickInteriorItem={handleClickItemInterior}
          onClickStep={handleClickStep}
          sitecoreData={sitecoreData}
        />
      );
    }
    if (step.stepValue === 5 && activeStep) {
      return (
        <Summary
          step={step}
          index={index}
          selectedCategoryCar={selectedCategoryCar}
          steps={stepList}
          onClickStep={handleClickStep}
          sitecoreData={sitecoreData}
        />
      );
    }
  };

  // 获取选中品牌的StepTitle和StepText
  const getSelectedBrandStepData = () => {
    if (!selectedBrand || !sitecoreData?.carType) {
      return { title: '', text: '' };
    }

    const selectedBrandData = sitecoreData.carType.find((brand) => brand.name === selectedBrand);
    if (!selectedBrandData?.fields) {
      return { title: '', text: '' };
    }

    return {
      title: selectedBrandData.fields.StepTitle?.value || '',
      text: selectedBrandData.fields.StepText?.value || '',
    };
  };

  const selectedBrandStepData = getSelectedBrandStepData();

  // 如果显示品牌选择页面
  if (showBrandSelection) {
    return (
      <div data-aos="fade-up" data-aos-delay="300">
        <ChooseBrand onBrandSelect={handleBrandSelect} sitecoreData={sitecoreData} />
      </div>
    );
  }

  return (
    <>
      {/* 返回品牌选择的按钮：仅在当前激活步�?> 1 时显�?*/}
      {!showBrandSelection && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 1000,
          }}
        >
          <button
            onClick={handleBackToBrandSelection}
            style={{
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: '1',
              padding: 0,
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.9)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.7)';
            }}
            aria-label="Back"
          >
            ←
          </button>
        </div>
      )}

      {/* 选择品牌后显�?TitleBlock，使用选中品牌的StepTitle和StepText */}
      <TitleBlock title={selectedBrandStepData.title} text={selectedBrandStepData.text} />

      <Steps steps={stepList} onClickStep={handleClickStep} />
      <div data-aos="fade-up" data-aos-delay="700">
        {stepList.map((step, index) => {
          return getStep(step, step.category, step.activeStep, index + 1);
        })}
      </div>
    </>
  );
}
